import { FOOT } from './format';

/*
 * The routing graph in memory: the trunk and whichever detail tiles were
 * loaded, merged by their global ids (an edge in two tiles is kept once),
 * with an adjacency list and a grid of edge segments for snapping a point to
 * the nearest way.
 *
 * Coordinates are degrees here (the files hold microdegrees). An edge's
 * polyline is node a, its shape points, node b.
 */
const GRID_DEG = 0.01; // ~1.1 km of latitude

export class RoutingGraph {
  constructor() {
    this.index = new Map();     // global node id -> local index
    this.lat = [];
    this.lng = [];
    this.nodeFlags = [];
    this.edgeSeen = new Set();  // global edge ids
    this.edges = [];            // {a, b, len, flags, cls, surf, grade, sac, shape: [[lat, lng], ...]}
    this.tiles = new Set();
    this.built = false;
  }

  node(globalId, latMicro, lonMicro, flags) {
    let at = this.index.get(globalId);
    if (at === undefined) {
      at = this.lat.length;
      this.index.set(globalId, at);
      this.lat.push(latMicro / 1e6);
      this.lng.push(lonMicro / 1e6);
      this.nodeFlags.push(flags);
    }
    return at;
  }

  /** Add one decoded file; `key` names it so it is never added twice. */
  addTile(tile, key) {
    if (key && this.tiles.has(key)) return false;
    if (key) this.tiles.add(key);
    const local = new Map();
    for (let i = 0; i < tile.nodeId.length; i += 1) {
      local.set(tile.nodeId[i], this.node(tile.nodeId[i], tile.nodeLat[i], tile.nodeLon[i], tile.nodeFlags[i]));
    }
    for (let k = 0; k < tile.edgeId.length; k += 1) {
      const id = tile.edgeId[k];
      if (this.edgeSeen.has(id)) continue;
      this.edgeSeen.add(id);
      const a = local.get(tile.edgeA[k]);
      const b = local.get(tile.edgeB[k]);
      const shape = [];
      let lat = tile.nodeLat[indexOf(tile, tile.edgeA[k])];
      let lon = tile.nodeLon[indexOf(tile, tile.edgeA[k])];
      for (let s = tile.shapeOff[k]; s < tile.shapeOff[k + 1]; s += 1) {
        lat += tile.shapeDlat[s];
        lon += tile.shapeDlon[s];
        shape.push([lat / 1e6, lon / 1e6]);
      }
      this.edges.push({ id, a, b, len: tile.edgeLen[k] / 10, flags: tile.edgeFlags[k], cls: tile.edgeCls[k],
        surf: tile.edgeSurf[k], grade: tile.edgeGrade[k], sac: tile.edgeSac[k], shape });
    }
    this.built = false;
    return true;
  }

  /** The adjacency list and the snapping grid, after the last tile was added. */
  build() {
    if (this.built) return this;
    const n = this.lat.length;
    const count = new Uint32Array(n + 1);
    for (const e of this.edges) { count[e.a + 1] += 1; count[e.b + 1] += 1; }
    for (let i = 0; i < n; i += 1) count[i + 1] += count[i];
    this.adjStart = count;
    this.adj = new Uint32Array(count[n]);
    const fill = count.slice(0, n);
    this.edges.forEach((e, k) => { this.adj[fill[e.a]++] = k; this.adj[fill[e.b]++] = k; });
    this.hasFoot = new Uint8Array(n);
    for (const e of this.edges) if (e.flags & FOOT) { this.hasFoot[e.a] = 1; this.hasFoot[e.b] = 1; }
    this.grid = new Map();
    this.edges.forEach((e, k) => {
      const pts = this.polyline(k);
      for (let s = 0; s + 1 < pts.length; s += 1) {
        const [la1, lo1] = pts[s];
        const [la2, lo2] = pts[s + 1];
        for (let r = cell(Math.min(la1, la2)); r <= cell(Math.max(la1, la2)); r += 1) {
          for (let c = cell(Math.min(lo1, lo2)); c <= cell(Math.max(lo1, lo2)); c += 1) {
            const key = `${r}:${c}`;
            let list = this.grid.get(key);
            if (!list) { list = []; this.grid.set(key, list); }
            list.push(k, s);
          }
        }
      }
    });
    this.built = true;
    return this;
  }

  /** [[lat, lng], ...] from node a to node b. */
  polyline(k) {
    const e = this.edges[k];
    return [[this.lat[e.a], this.lng[e.a]], ...e.shape, [this.lat[e.b], this.lng[e.b]]];
  }

  /** Segments [edge, segment] in grid cells within `radiusM` of a point. */
  near(lat, lng, radiusM) {
    const dLat = radiusM / 111_320;
    const dLng = radiusM / (111_320 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
    const out = [];
    for (let r = cell(lat - dLat); r <= cell(lat + dLat); r += 1) {
      for (let c = cell(lng - dLng); c <= cell(lng + dLng); c += 1) {
        const list = this.grid.get(`${r}:${c}`);
        if (list) for (let i = 0; i < list.length; i += 2) out.push(list[i], list[i + 1]);
      }
    }
    return out;
  }
}

function cell(deg) {
  return Math.floor(deg / GRID_DEG);
}

// Node arrays are sorted by global id (tiles.py), so a binary search finds one.
function indexOf(tile, globalId) {
  let lo = 0;
  let hi = tile.nodeId.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = tile.nodeId[mid];
    if (v === globalId) return mid;
    if (v < globalId) lo = mid + 1; else hi = mid - 1;
  }
  throw new Error('an edge names a node its tile does not hold');
}
